import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Login } from './pages/Login';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { AcceptInvite } from './pages/AcceptInvite';
import { Layout } from './components/Layout';
import { ProtectedRoute } from './components/ProtectedRoute';

// Rollout #10 — route-level code splitting. Every page used to be statically
// imported, so Login/Dashboard downloaded the JS for all admin pages + the
// 2,220-line ProjectDetail up front. The auth pages (first paint, pre-login)
// stay eager; everything behind ProtectedRoute is React.lazy so Vite emits one
// chunk per page, fetched on first navigation. A failed chunk load (stale
// deploy) is caught by the ErrorBoundary (#5), which offers a reload.
const named = <T,>(p: Promise<Record<string, T>>, key: string) =>
  p.then((m) => ({ default: m[key] as T }));

const Dashboard = lazy(() => named(import('./pages/Dashboard'), 'Dashboard'));
const AdminCompanyProfile = lazy(() => named(import('./pages/AdminCompanyProfile'), 'AdminCompanyProfile'));
const Opportunities = lazy(() => named(import('./pages/Opportunities'), 'Opportunities'));
const OpportunityDetail = lazy(() => named(import('./pages/OpportunityDetail'), 'OpportunityDetail'));
const AdminSources = lazy(() => named(import('./pages/AdminSources'), 'AdminSources'));
const AdminOffices = lazy(() => named(import('./pages/AdminOffices'), 'AdminOffices'));
const ProjectList = lazy(() => named(import('./pages/ProjectList'), 'ProjectList'));
const ProjectDetail = lazy(() => named(import('./pages/ProjectDetail'), 'ProjectDetail'));
const AdminOps = lazy(() => named(import('./pages/AdminOps'), 'AdminOps'));
const AdminLibrary = lazy(() => named(import('./pages/AdminLibrary'), 'AdminLibrary'));
const AdminUsers = lazy(() => named(import('./pages/AdminUsers'), 'AdminUsers'));
const AdminTeams = lazy(() => named(import('./pages/AdminTeams'), 'AdminTeams'));
const Pricing = lazy(() => named(import('./pages/Pricing'), 'Pricing'));
const SavedSearches = lazy(() => named(import('./pages/SavedSearches'), 'SavedSearches'));
const BriefViewer = lazy(() => named(import('./pages/BriefViewer'), 'BriefViewer'));
const CaptainRun = lazy(() => named(import('./pages/CaptainRun'), 'CaptainRun'));
const PostmortemReader = lazy(() => named(import('./pages/PostmortemReader'), 'PostmortemReader'));
const OpportunityExplorer = lazy(() => named(import('./pages/OpportunityExplorer'), 'OpportunityExplorer'));
const Walkthrough = lazy(() => named(import('./pages/Walkthrough'), 'Walkthrough'));
const Help = lazy(() => named(import('./pages/Help'), 'Help'));
const AdminChat = lazy(() => named(import('./pages/AdminChat'), 'AdminChat'));
const AdminMemory = lazy(() => named(import('./pages/AdminMemory'), 'AdminMemory'));
const AdminGCs = lazy(() => named(import('./pages/AdminGCs'), 'AdminGCs'));
const AdminSettings = lazy(() => named(import('./pages/AdminSettings'), 'AdminSettings'));

export function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />

          <Route element={<ProtectedRoute />}>
            <Route element={<Layout />}>
              <Route index element={<Dashboard />} />
              <Route path="/opportunities" element={<Opportunities />} />
              <Route path="/opportunities/:id" element={<OpportunityDetail />} />
              <Route path="/saved-searches" element={<SavedSearches />} />
              <Route path="/watchlist" element={<SavedSearches />} />
              <Route path="/projects" element={<ProjectList />} />
              <Route path="/projects/:id" element={<ProjectDetail />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/library" element={<AdminLibrary />} />
              <Route path="/briefs/:id" element={<BriefViewer />} />
              <Route path="/projects/:projectId/captain/:runId" element={<CaptainRun />} />
              <Route path="/postmortems/:id" element={<PostmortemReader />} />
              <Route path="/opportunities/:id/explore" element={<OpportunityExplorer />} />
              <Route path="/projects/:projectId/walkthrough/:documentId/:page" element={<Walkthrough />} />
              <Route path="/help" element={<Help />} />
            </Route>
          </Route>

          <Route element={<ProtectedRoute requireRole="SENIOR_ESTIMATOR" />}>
            <Route element={<Layout />}>
              {/* Lead Estimator + Admin */}
              <Route path="/admin/teams" element={<AdminTeams />} />
            </Route>
          </Route>

          <Route element={<ProtectedRoute requireRole="ADMIN" />}>
            <Route element={<Layout />}>
              <Route path="/admin/users" element={<AdminUsers />} />
              <Route path="/admin/offices" element={<AdminOffices />} />
              <Route path="/admin/company" element={<AdminCompanyProfile />} />
              <Route path="/admin/settings" element={<AdminSettings />} />
              <Route path="/admin/sources" element={<AdminSources />} />
              <Route path="/admin/ops" element={<AdminOps />} />
              <Route path="/admin/chat" element={<AdminChat />} />
              <Route path="/admin/memory" element={<AdminMemory />} />
              <Route path="/admin/gcs" element={<AdminGCs />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
